import { useCallback } from "react";
import { ethers } from "ethers";
import {
  getBridgeStatus,
  submitBridgeCancel,
  submitBridgeExecute,
} from "../../utils/relayer-client";
import { EVM_CONFIG } from "../../config";
import { executeFlatJoinSplitV2Bridge } from "../../joinsplit-v2/operations";
import type { FlatJoinSplitV2BridgeData } from "@stealthos/sdk";

/** Settlement window bound into the bridge proof. */
const FLAT_BRIDGE_DEADLINE_SECONDS = 600;
import {
  computeBridgeOrderExpectationDigest,
  computePrivateBridgeActionHash,
  deriveTokenHash,
  normalizeBridgeOrderExpectation,
} from "../../operations/settlement-helpers";
import type { BaseStoredNote } from "../types";
import type { JoinSplitNote } from "../../joinsplit/types";

interface UseBaseBridgeOperationParams {
  signer: ethers.Signer | null;
  spendingKeys: any;
  notes: BaseStoredNote[];
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
  setStatus: (
    status: { type: "success" | "error" | "info"; message: string } | null,
  ) => void;
  setLoading: (loading: boolean) => void;
}

async function sleep(ms: number) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

function bridgeQuoteFeeConfig(quote: any, sourceSpendAmount: bigint) {
  const feeConfig = quote?.bridge_fee_config || quote?.bridgeFeeConfig || {};
  const protocolFeeBps = BigInt(
    feeConfig.protocol_bridge_fee_bps ??
      feeConfig.protocolBridgeFeeBps ??
      quote?.protocol_bridge_fee_bps ??
      quote?.protocolBridgeFeeBps,
  );
  const partnerFeeBps = BigInt(
    feeConfig.partner_bridge_fee_bps ??
      feeConfig.partnerBridgeFeeBps ??
      quote?.partner_bridge_fee_bps ??
      quote?.partnerBridgeFeeBps,
  );
  const protocolFeeRecipient =
    feeConfig.protocol_fee_recipient ||
    feeConfig.protocolFeeRecipient ||
    quote?.protocol_fee_recipient ||
    quote?.protocolFeeRecipient;
  const partnerFeeRecipient =
    feeConfig.partner_fee_recipient ||
    feeConfig.partnerFeeRecipient ||
    quote?.partner_fee_recipient ||
    quote?.partnerFeeRecipient;
  const bridgedAmountRaw =
    feeConfig.bridged_amount ??
    feeConfig.bridgedAmount ??
    quote?.bridged_amount ??
    quote?.bridgedAmount ??
    quote?.normalized_source_amount ??
    quote?.normalizedSourceAmount;

  if (
    !ethers.isAddress(protocolFeeRecipient) ||
    !ethers.isAddress(partnerFeeRecipient)
  ) {
    throw new Error("Bridge quote missing fee recipient binding");
  }
  const bridgedAmount = BigInt(bridgedAmountRaw);
  if (bridgedAmount <= 0n || bridgedAmount > sourceSpendAmount) {
    throw new Error("Bridge quote fee binding has invalid bridged amount");
  }
  return {
    protocolFeeBps,
    partnerFeeBps,
    protocolFeeRecipient,
    partnerFeeRecipient,
    bridgedAmount,
  };
}

export function useBaseBridgeOperation({
  signer,
  spendingKeys,
  notes,
  addLog,
  setStatus,
  setLoading,
}: UseBaseBridgeOperationParams) {
  type BridgeOperationResult = {
    success: boolean;
    transactionHash?: string;
    journalId?: string;
    orderId?: string;
    status?: any;
    pending?: boolean;
    error?: string;
    spentCommitments?: string[];
    changeNote?: JoinSplitNote;
  };
  const handleBridge = useCallback(
    async (params: {
      chainId: number;
      sourceToken: string;
      sourceSpendAmount: string;
      sourceTokenDecimals: number;
      quote: any;
      onOrderUpdate?: (state: any) => void;
    }): Promise<BridgeOperationResult> => {
      const {
        chainId,
        sourceToken,
        sourceSpendAmount,
        sourceTokenDecimals,
        quote,
        onOrderUpdate,
      } = params;
      const orderIdFromQuote = quote?.order_id || quote?.orderId;
      if (!orderIdFromQuote) {
        setStatus({ type: "error", message: "No bridge quote available" });
        return { success: false };
      }
      if (!signer || !spendingKeys) {
        setStatus({
          type: "error",
          message: "Connect your wallet first",
        });
        return { success: false };
      }
      let sourceSpentCommitments: string[] = [];
      let sourceChangeNote: JoinSplitNote | undefined;
      try {
        setLoading(true);
        const sourceAuthority =
          quote?.source_authority || quote?.sourceAuthority;
        if (!sourceAuthority || !ethers.isAddress(sourceAuthority)) {
          throw new Error("Bridge quote missing source authority");
        }
        const poolAddress = EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT;
        if (!poolAddress || !ethers.isAddress(poolAddress)) {
          throw new Error("Shielded pool address is not configured");
        }
        const sourceSpendAmountBigInt = BigInt(sourceSpendAmount);
        if (sourceSpendAmountBigInt <= 0n) {
          throw new Error("Invalid bridge source amount");
        }

        addLog(
          `Atomic bridge target pool: ${poolAddress.slice(0, 10)}...`,
          "info",
        );
        addLog("Preparing source private spend for bridge...", "info");
        const sourceTokenHash = await deriveTokenHash(sourceToken, "erc20");
        const sourceTokenHashHex =
          "0x" + sourceTokenHash.toString(16).padStart(64, "0");
        const quoteTx = quote?.tx || {};
        if (!quoteTx.to || !quoteTx.data) {
          throw new Error("Bridge quote missing target calldata");
        }
        const orderExpectation = normalizeBridgeOrderExpectation(quote);

        // The flat pool binds target, calldata, callValue, expected order id,
        // the full order expectation and the deadline into the proof via the
        // bridge aux hash, so none of the Nova action-hash construction below
        // applies. Nothing is re-shielded here: the value leaves the chain.
        const callValue = BigInt(quoteTx.value || "0");
        const relay =
          EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS && callValue === 0n;
        const relayFee = relay
          ? (sourceSpendAmountBigInt *
              BigInt(EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS)) /
            10_000n
          : 0n;
        if (relay && relayFee === 0n) {
          throw new Error(
            "Bridge amount is too small to cover the relayer fee",
          );
        }
        const result = await executeFlatJoinSplitV2Bridge({
          signer,
          spendingKeys,
          notes,
          tokenAddress: sourceToken,
          amount: sourceSpendAmountBigInt,
          target: quoteTx.to,
          bridgeCallData: quoteTx.data as `0x${string}`,
          expectedOrderId: orderIdFromQuote as `0x${string}`,
          // Field names and runtime forms already match the SDK struct:
          // normalizeBridgeOrderExpectation returns bigints for the two
          // numeric fields, a checksummed address for givePatchAuthoritySrc,
          // and normalizeBytes-validated `0x` even-length hex for the rest.
          // The cast only supplies the template-literal narrowing TypeScript
          // cannot infer from a `string` return.
          expectation:
            orderExpectation as FlatJoinSplitV2BridgeData["expectation"],
          deadline:
            BigInt(Math.floor(Date.now() / 1000)) +
            BigInt(FLAT_BRIDGE_DEADLINE_SECONDS),
          callValue,
          fee: relayFee,
          relay,
          onLog: addLog,
        });
        if (!result.success) {
          if (result.journalId || result.txHash) {
            const message =
              result.error ||
              "Bridge submission requires journal recovery before retrying.";
            addLog(message, "warning", result.txHash);
            setStatus({ type: "error", message });
            return {
              success: false,
              pending: true,
              transactionHash: result.txHash,
              journalId: result.journalId,
              orderId: orderIdFromQuote,
              error: message,
            };
          }
          throw new Error(result.error || "Private bridge failed");
        }
        addLog(`Bridge order submitted: ${orderIdFromQuote}`, "success");
        setStatus({
          type: "success",
          message: `Private bridge settled. Track order ${orderIdFromQuote}.`,
        });
        return {
          success: true,
          transactionHash: result.txHash,
          journalId: result.journalId,
          orderId: orderIdFromQuote,
          spentCommitments: result.spentNotes.map((note) => note.commitment),
          changeNote: result.changeNote,
        };
      } catch (err: any) {
        const msg = err?.message || "Bridge execution failed";
        addLog(`Bridge failed: ${msg}`, "error");
        setStatus({ type: "error", message: msg });
        return {
          success: false,
          error: msg,
          transactionHash: err?.txHash,
          journalId: err?.journalId,
          orderId: orderIdFromQuote,
          pending: Boolean(err?.txHash || err?.journalId),
        };
      } finally {
        setLoading(false);
      }
    },
    [addLog, notes, setLoading, setStatus, signer, spendingKeys],
  );

  const handleBridgeCancel = useCallback(
    async (orderId: string, sourceAuthority?: string) => {
      try {
        setLoading(true);
        addLog(`Cancelling bridge order: ${orderId}`, "info");
        const result = await submitBridgeCancel(orderId, sourceAuthority);
        addLog(
          "Bridge cancel submitted",
          "success",
          result?.cancel_tx_hash || result?.cancelTxHash,
        );
        setStatus({ type: "info", message: "Bridge cancellation submitted" });
        return { success: true, result };
      } catch (err: any) {
        const msg = err?.message || "Bridge cancel failed";
        addLog(`Bridge cancel failed: ${msg}`, "error");
        setStatus({ type: "error", message: msg });
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    },
    [addLog, setLoading, setStatus],
  );

  return { handleBridge, handleBridgeCancel };
}
